import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import theme from "@utils/theme";
import {
  ShoppingCart,
  Currency,
  Star,
  Package,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  LucideIcon,
  ShoppingBag,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  DashboardWrapper,
  HeaderArea,
  HeaderText,
  MetricsGrid,
  MetricCard,
  MetricHeader,
  MetricIcon,
  MetricValue,
  MetricLabel,
  MainDashboard,
  ContentCard,
  CardHeader,
  CardTitle,
  ListWrapper,
  ListItem,
  ListIcon,
  ListContent,
  ListMainText,
  ListSubText,
  BottomSection,
  OverviewList,
  OverviewItem,
  OverviewLabel,
  OverviewValue,
  EmptyState,
  ChartSection,
  ChartToggleContainer,
  ChartToggleButton,
  ChartControls,
  ChartSummaryRow,
  ChartSummaryItem,
} from "./style";
import {
  Store as StoreApi,
  Order as OrderApi,
  Product as ProductApi,
} from "@apis/index";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";

const LoadingPulse = ({
  width = "100%",
  height = "20px",
}: {
  width?: string | number;
  height?: string | number;
}) => (
  <div
    style={{
      width,
      height,
      background: "#f3f4f6",
      borderRadius: "4px",
      animation: "pulse 1.5s infinite",
    }}
  />
);

const StoreDashboard = () => {
  const navigate = useNavigate();
  const { data: user } = useSelector((state: RootState) => state.user);
  const [analytics, setAnalytics] = useState<any>(null);
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [topProducts, setTopProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"earnings" | "orders">("earnings");
  const [chartPeriod, setChartPeriod] = useState<"overview" | "today" | "week" | "month" | "year">("overview");

  const fetchData = async () => {
    try {
      setLoading(true);
      // Backend should use the storeId from the token if not provided
      const [analyticsRes, ordersRes, productsRes] = await Promise.all([
        StoreApi.getStoreAnalytics(),
        OrderApi.list({ params: { limit: 5 } }),
        ProductApi.list({ params: { limit: 5, sort: "popular" } }),
      ]);

      setAnalytics(analyticsRes);
      setRecentOrders(ordersRes.data || []);
      setTopProducts(productsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch store dashboard data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const metrics = [
    {
      label: "Total Sales",
      value: `रू ${analytics?.earnings?.total?.toLocaleString() || "0"}`,
      icon: Currency,
      background: theme.orange.orange50,
      color: theme.orange.orange500,
      link: "/store/transaction/my-wallet",
    },
    {
      label: "Today's Earnings",
      value: `रू ${analytics?.earnings?.today?.storeEarnings?.toLocaleString() || "0"}`,
      icon: TrendingUp,
      background: "#E9FFF1",
      color: "#22C55E",
      link: "/store/transaction/my-wallet",
    },
    {
      label: "Active Orders",
      value: analytics?.orderOverview?.totalOngoingOrders || 0,
      icon: ShoppingBag,
      background: "#DBEAFE",
      color: "#3B82F6",
      link: "/store/orders/order-placed",
    },
    {
      label: "Total Products",
      value: analytics?.productOverview?.total || "0",
      icon: Package,
      background: "#FEF0CD",
      color: "#E2AF2C",
      link: "/store/product-management/products",
    },
  ];

  const periodLabels = {
    overview: "Overview",
    today: "Today",
    week: "This Week",
    month: "This Month",
    year: "This Year",
  };

  const getSelectedPeriodData = () => {
    const periodMap: Record<string, any> = {
      today: analytics?.earnings?.today,
      week: analytics?.earnings?.thisWeek,
      month: analytics?.earnings?.thisMonth,
      year: analytics?.earnings?.thisYear,
    };
    return periodMap[chartPeriod] || null;
  };

  const getChartData = () => {
    if (chartPeriod === "overview") {
      // Overview: show all periods as comparison
      const periods = [
        { key: "today", label: "Today" },
        { key: "thisWeek", label: "This Week" },
        { key: "thisMonth", label: "This Month" },
        { key: "thisYear", label: "This Year" },
      ];
      return periods.map((p) => {
        const d = analytics?.earnings?.[p.key];
        return {
          name: p.label,
          earnings: d?.storeEarnings || 0,
          orders: d?.totalOrders || 0,
          sales: d?.totalSales || 0,
          commission: d?.commission || 0,
        };
      });
    }

    // Single period: show breakdown bars
    const d = getSelectedPeriodData();
    return [
      { name: "Earnings", value: d?.storeEarnings || 0 },
      { name: "Total Sales", value: d?.totalSales || 0 },
      { name: "Commission", value: d?.commission || 0 },
      { name: "Discount", value: d?.discount || 0 },
    ];
  };

  const chartData = getChartData();
  const selectedPeriodData = getSelectedPeriodData();

  return (
    <DashboardWrapper>
      <HeaderArea>
        <HeaderText>
          <h1>Store Dashboard</h1>
          <p>Welcome back! Here's what's happening with your store today.</p>
        </HeaderText>
      </HeaderArea>

      <MetricsGrid>
        {metrics.map((metric, index) => (
          <MetricCard key={index} onClick={() => navigate(metric.link)}>
            <MetricHeader>
              <MetricIcon background={metric.background} color={metric.color}>
                <metric.icon size={18} />
              </MetricIcon>
            </MetricHeader>
            <MetricValue>
              {loading ? <LoadingPulse width={80} /> : metric.value}
            </MetricValue>
            <MetricLabel>{metric.label}</MetricLabel>
          </MetricCard>
        ))}
      </MetricsGrid>

      <ChartSection>
        <ContentCard>
          <CardHeader>
            <CardTitle>
              {chartPeriod === "overview"
                ? `${activeTab === "earnings" ? "Earnings" : "Orders"} Overview`
                : `${periodLabels[chartPeriod]} — ${activeTab === "earnings" ? "Earnings" : "Orders"} Breakdown`}
            </CardTitle>
            <ChartControls>
              <ChartToggleContainer>
                {(["overview", "today", "week", "month", "year"] as const).map((period) => (
                  <ChartToggleButton
                    key={period}
                    active={chartPeriod === period}
                    onClick={() => setChartPeriod(period)}
                  >
                    {periodLabels[period]}
                  </ChartToggleButton>
                ))}
              </ChartToggleContainer>
              <ChartToggleContainer>
                <ChartToggleButton
                  active={activeTab === "earnings"}
                  onClick={() => setActiveTab("earnings")}
                >
                  Earnings
                </ChartToggleButton>
                <ChartToggleButton
                  active={activeTab === "orders"}
                  onClick={() => setActiveTab("orders")}
                >
                  Orders
                </ChartToggleButton>
              </ChartToggleContainer>
            </ChartControls>
          </CardHeader>

          {chartPeriod !== "overview" && selectedPeriodData && (
            <ChartSummaryRow>
              <ChartSummaryItem>
                <span>Earnings</span>
                <span>रू {selectedPeriodData?.storeEarnings?.toLocaleString() || "0"}</span>
              </ChartSummaryItem>
              <ChartSummaryItem>
                <span>Total Sales</span>
                <span>रू {selectedPeriodData?.totalSales?.toLocaleString() || "0"}</span>
              </ChartSummaryItem>
              <ChartSummaryItem>
                <span>Orders</span>
                <span>{selectedPeriodData?.totalOrders || 0}</span>
              </ChartSummaryItem>
              <ChartSummaryItem>
                <span>Commission</span>
                <span>रू {selectedPeriodData?.commission?.toLocaleString() || "0"}</span>
              </ChartSummaryItem>
              <ChartSummaryItem>
                <span>Discount</span>
                <span>रू {selectedPeriodData?.discount?.toLocaleString() || "0"}</span>
              </ChartSummaryItem>
            </ChartSummaryRow>
          )}

          <div style={{ width: "100%", height: 300 }}>
            {loading ? (
              <LoadingPulse height={300} />
            ) : chartPeriod === "overview" ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={chartData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={theme.orange.orange500}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={theme.orange.orange500}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="name"
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                  />
                  <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} />
                  <Tooltip
                    contentStyle={{
                      background: "white",
                      border: "1px solid #f3f4f6",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                    formatter={(value: any, name: string) => [
                      activeTab === "earnings"
                        ? `रू ${value.toLocaleString()}`
                        : `${value} orders`,
                      activeTab === "earnings" ? "Earnings" : "Orders",
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey={activeTab === "earnings" ? "earnings" : "orders"}
                    stroke={theme.orange.orange500}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorValue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="name"
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                  />
                  <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} />
                  <Tooltip
                    contentStyle={{
                      background: "white",
                      border: "1px solid #f3f4f6",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                    formatter={(value: any) => [
                      `रू ${value.toLocaleString()}`,
                      "",
                    ]}
                  />
                  <Bar
                    dataKey="value"
                    fill={theme.orange.orange500}
                    radius={[6, 6, 0, 0]}
                    maxBarSize={60}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </ContentCard>
      </ChartSection>

      <MainDashboard>
        <ContentCard>
          <CardHeader>
            <CardTitle>Recent Orders</CardTitle>
            <ShoppingCart size={16} color="#6b7280" />
          </CardHeader>
          <ListWrapper>
            {loading ? (
              [1, 2, 3].map((i) => <LoadingPulse key={i} height={60} />)
            ) : recentOrders.length > 0 ? (
              recentOrders.map((order) => (
                <ListItem key={order.id}>
                  <ListIcon>
                    {order.status === "Delivered" ? (
                      <CheckCircle2 size={14} />
                    ) : order.status === "Cancelled" || order.status === "Canceled" ? (
                      <AlertCircle size={14} />
                    ) : (
                      <Clock size={14} />
                    )}
                  </ListIcon>
                  <ListContent>
                    <ListMainText>
                      Order #{order.id} • रू{" "}
                      {order.actualAmount?.toLocaleString()}
                    </ListMainText>
                    <ListSubText>
                      {new Date(order.createdAt).toLocaleString()} •{" "}
                      {order.status}
                    </ListSubText>
                  </ListContent>
                </ListItem>
              ))
            ) : (
              <EmptyState>No recent orders found</EmptyState>
            )}
          </ListWrapper>
        </ContentCard>

        <ContentCard>
          <CardHeader>
            <CardTitle>Order Summary</CardTitle>
            <Package size={16} color="#6b7280" />
          </CardHeader>
          <OverviewList>
            <OverviewItem>
              <OverviewLabel>Pending</OverviewLabel>
              <OverviewValue>
                {analytics?.orderOverview?.allTime?.["Order Placed"] || 0}
              </OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Confirmed</OverviewLabel>
              <OverviewValue>
                {analytics?.orderOverview?.allTime?.["Order Confirmed"] || 0}
              </OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Processing</OverviewLabel>
              <OverviewValue>
                {analytics?.orderOverview?.allTime?.["Order Processing"] || 0}
              </OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Delivered</OverviewLabel>
              <OverviewValue>
                {analytics?.orderOverview?.allTime?.["Delivered"] || 0}
              </OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Cancelled</OverviewLabel>
              <OverviewValue>
                {analytics?.orderOverview?.allTime?.["Cancelled"] || analytics?.orderOverview?.allTime?.["Canceled"] || 0}
              </OverviewValue>
            </OverviewItem>
          </OverviewList>
        </ContentCard>
      </MainDashboard>

      <BottomSection>
        <ContentCard>
          <CardHeader>
            <CardTitle>Top Selling Products</CardTitle>
            <Star size={16} color="#6b7280" />
          </CardHeader>
          <ListWrapper>
            {loading ? (
              [1, 2, 3].map((i) => <LoadingPulse key={i} height={60} />)
            ) : topProducts.length > 0 ? (
              topProducts.map((product) => (
                <ListItem key={product.id}>
                  <ListIcon>
                    <Package size={14} />
                  </ListIcon>
                  <ListContent>
                    <ListMainText>{product.name}</ListMainText>
                    <ListSubText>
                      Price: रू {product.price?.toLocaleString()} • Rating:{" "}
                      {product.avgRating || 0}/5
                    </ListSubText>
                  </ListContent>
                </ListItem>
              ))
            ) : (
              <EmptyState>No items found</EmptyState>
            )}
          </ListWrapper>
        </ContentCard>

        <ContentCard>
          <CardHeader>
            <CardTitle>Product Inventory</CardTitle>
            <AlertCircle size={16} color="#6b7280" />
          </CardHeader>
          <OverviewList>
            <OverviewItem>
              <OverviewLabel>Total Products</OverviewLabel>
              <OverviewValue>
                {analytics?.productOverview?.total || 0}
              </OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Active Products</OverviewLabel>
              <OverviewValue>
                {analytics?.productOverview?.active || 0}
              </OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Inactive Products</OverviewLabel>
              <OverviewValue>
                {analytics?.productOverview?.inactive || 0}
              </OverviewValue>
            </OverviewItem>
          </OverviewList>
        </ContentCard>
      </BottomSection>
    </DashboardWrapper>
  );
};

export default StoreDashboard;
